import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Card chrome shared across detail pages: a bordered card with an icon + uppercase
 * section title and an optional header action (e.g. an edit pencil), over a body.
 * Replaces the copy-pasted `rounded-lg border bg-card` + header markup.
 */
export function SectionCard({
  icon: Icon,
  title,
  action,
  className,
  bodyClassName = 'p-4',
  children,
}: {
  icon: LucideIcon;
  title: string;
  action?: React.ReactNode;
  className?: string;
  /** Override the body wrapper classes (pass e.g. `p-0` when branches own padding). */
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border bg-card', className)}>
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="truncate text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </h2>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/**
 * A single label-over-value cell for definition grids. Tight, scannable, and
 * truncation-safe — pair inside a `<dl className="grid …">`.
 */
export function DetailField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

/** Em-dash placeholder for an unset field. */
export function EmptyValue() {
  return <span className="text-muted-foreground">—</span>;
}
